/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useEffect, useState } from 'react';
import { Check, Plus, Shirt, X } from 'lucide-react';
import { ApiError, api } from '../lib/api';
import { errorText } from '../lib/errors';
import {
  FULL_ACCESS_CHANGED_TEXT,
  FULL_ACCESS_HINT,
  FULL_ACCESS_SAVED_TEXT,
  FULL_ACCESS_UNREADABLE_TEXT,
  emailAddRefusal,
  fullAccessChanged,
  fullAccessSaveBody,
  groupIdRefusal,
  groupStatusText,
  normalizeFullAccessEmail,
  type FullAccessView,
} from '../lib/fullOutfitAccess';
import { Button, Callout, Field, Icon, Input, Panel, Skeleton } from '../ui';
import { SaveNote } from './SaveNote';

export interface FullOutfitAccessSectionProps {
  /** Writes are off: the admin is an auditor, or the card is busy. */
  locked: boolean;
}

type Read =
  | { status: 'loading' }
  /** A service without the route (an older one): the section is not shown. */
  | { status: 'absent' }
  | { status: 'failed'; text: string }
  | { status: 'loaded'; view: FullAccessView };

/**
 * "Always see every outfit" on the outfit gallery page (owner, 2026-10-09: "MY ACCOUNT should show every outfit
 * avaiable all the time"; "for all the time all outfit access lets use security group USER-PRINCIPALS"): the people IT
 * lists by e-mail (Add, Remove) and the Entra group whose members have it too, with whether the group is in effect now
 * (it waits for The Bridge connection for gyro-svc). One Save sends the whole setting with the revision the section
 * loaded. The section reads its own setting, and hides on a service without it.
 */
export function FullOutfitAccessSection({ locked }: FullOutfitAccessSectionProps) {
  const [read, setRead] = useState<Read>({ status: 'loading' });
  const [emails, setEmails] = useState<string[]>([]);
  const [group, setGroup] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  /** Show what the service holds: the list and the group as stored. */
  const show = useCallback((view: FullAccessView) => {
    setRead({ status: 'loaded', view });
    setEmails([...view.emails]);
    setGroup(view.groupId ?? '');
  }, []);

  useEffect(() => {
    let live = true;
    api.desktopMascotFullAccess().then(
      (view) => {
        if (!live) return;
        if (view === null) setRead({ status: 'absent' });
        else show(view);
      },
      (error: unknown) => {
        if (live) setRead({ status: 'failed', text: errorText(error, FULL_ACCESS_UNREADABLE_TEXT) });
      },
    );
    return () => {
      live = false;
    };
  }, [show]);

  if (read.status === 'absent') return null;
  const view = read.status === 'loaded' ? read.view : null;
  const disabled = locked || busy || view === null;
  const dirty = view !== null && fullAccessChanged(emails, group, view);
  const addRefusal = newEmail.trim() === '' ? null : emailAddRefusal(newEmail, emails);
  const groupRefusal = groupIdRefusal(group);
  const canAdd = !disabled && newEmail.trim() !== '' && addRefusal === null;

  const add = () => {
    if (!canAdd) return;
    setEmails((current) => [...current, normalizeFullAccessEmail(newEmail)]);
    setNewEmail('');
    setNote(null);
  };
  const cancel = () => {
    if (!view) return;
    setEmails([...view.emails]);
    setGroup(view.groupId ?? '');
    setNote(null);
  };
  const save = async () => {
    if (!view) return;
    if (groupRefusal) {
      setNote({ ok: false, text: groupRefusal });
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      show(await api.saveDesktopMascotFullAccess(fullAccessSaveBody(emails, group, view.revision)));
      setNote({ ok: true, text: FULL_ACCESS_SAVED_TEXT });
    } catch (error) {
      const code = error instanceof ApiError && typeof error.body?.error === 'string' ? error.body.error : undefined;
      if (code === 'mascot_full_access_changed') {
        setNote({ ok: false, text: FULL_ACCESS_CHANGED_TEXT });
        try {
          const fresh = await api.desktopMascotFullAccess();
          if (fresh) show(fresh);
        } catch {
          // The note already says to check it again; a reload shows it.
        }
      } else {
        setNote({ ok: false, text: errorText(error, 'Nothing was saved.') });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      heading="h3"
      titleId="mascot-full-access-title"
      title={<span className="ui-title-icon"><Icon icon={Shirt} size="md" />Always see every outfit</span>}
      description={FULL_ACCESS_HINT}
    >
        {read.status === 'loading' && <div className="ui-form" aria-hidden="true"><Skeleton size="md" width="240px" /><Skeleton size="lg" /></div>}
        {read.status === 'failed' && <Callout tone="warn" role="alert">{read.text}</Callout>}
        {view && (
          <fieldset disabled={disabled} className="ui-fieldset">
            <div className="ui-form">
              <Field label="People">
                {emails.length === 0 ? (
                  <p className="ui-cell-sub">Nobody is listed.</p>
                ) : (
                  <ul aria-label="People who always see every outfit" className="ui-people-list">
                    {emails.map((email) => (
                      <li key={email} data-email={email}>
                        <span>{email}</span>
                        <Button variant="ghost" size="sm" icon={X} aria-label={`Remove ${email}`} onClick={() => { setEmails((current) => current.filter((known) => known !== email)); setNote(null); }}>Remove</Button>
                      </li>
                    ))}
                  </ul>
                )}
              </Field>
              <Field label="Add a person" error={addRefusal ?? undefined}>
                <div className="ui-actions">
                  <Input
                    id="mascot-full-access-email"
                    type="email"
                    placeholder="name@exante360.com"
                    value={newEmail}
                    className="ui-w-md"
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                  />
                  <Button size="sm" icon={Plus} disabled={!canAdd} onClick={add}>Add</Button>
                </div>
              </Field>
              <Field
                label="Group (object id)"
                help="Its members see every outfit too. The group takes effect once The Bridge connection for gyro-svc is live; leave it empty for no group."
                error={groupRefusal ?? undefined}
              >
                <Input
                  id="mascot-full-access-group"
                  className="is-mono ui-w-lg"
                  placeholder="00000000-0000-0000-0000-000000000000"
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                />
              </Field>
              <p className={view.group.inEffect ? 'ui-note is-ok' : 'ui-note is-warn'} data-group-state={view.group.state}>
                <span>{groupStatusText(view.group)}</span>
              </p>
              <div className="ui-actions">
                <Button variant="primary" size="sm" icon={Check} busy={busy} disabled={!dirty || disabled || groupRefusal !== null} onClick={() => void save()}>Save</Button>
                <Button variant="ghost" size="sm" icon={X} disabled={!dirty || disabled} onClick={cancel}>Cancel</Button>
                {dirty && <span className="ui-cell-sub">Not saved yet.</span>}
              </div>
            </div>
          </fieldset>
        )}
      <SaveNote note={note} />
    </Panel>
  );
}
